import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import type { LlmSetting, LlmSettingUpdate } from "../../api/llmSettingsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Human labels for provider ids. Keyed by id rather than positional so a
 *  provider the server adds later still renders (as its raw id) instead of a
 *  blank option. */
const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  fireworks: "Fireworks AI",
};

/** Model ids are long and provider-qualified (Fireworks ids are full paths),
 *  so show the tail in the dropdown and keep the id as the value. */
function modelLabel(id: string): string {
  return id.includes("/") ? id.split("/").pop() || id : id;
}

/** The sentinel for "inherit". An empty string is the MUI Select's own
 *  placeholder value, so it cannot double as a real choice. */
const INHERIT = "__inherit__";

/** Which provider and model this organization's turns run on.
 *
 *  Admin-gated for writes by the API, which enforces it independently — this
 *  panel renders the 403 rather than hiding it, because a blank panel is
 *  indistinguishable from a broken one.
 *
 *  An unset field INHERITS the deployment default rather than being empty,
 *  which is why every dropdown carries an explicit "Use the default" option:
 *  clearing has to be expressible, or an admin could never undo a choice.
 */
export function LlmSettings() {
  const api = useAuthedApi();
  const [setting, setSetting] = useState<LlmSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load ONCE on mount, deliberately not keyed on `api`.
  //
  // useAuthedApi() returns a fresh object every render, so a useCallback that
  // depends on it changes identity on every state update — and an effect
  // keyed on that callback re-runs on every state update too. That is not
  // just wasteful: the reload calls setError(null), which WIPED the error a
  // failed save had just set. The panel flashed the failure and then silently
  // cleared it, so a save that did not happen looked like one that did.
  //
  // The api object is stable in behaviour even though its identity is not,
  // so the empty dep list is correct rather than a lint escape.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void (async () => {
      try {
        setSetting(await api.llmSettings.load());
        setError(null);
      } catch (e) {
        setError(message(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback(
    async (patch: LlmSettingUpdate, what: string) => {
      setSaving(true);
      setNotice(null);
      try {
        // The response replaces local state, not the submitted patch: the
        // server owns normalisation and recomputes `source`, which flips to
        // "platform" the moment a field is cleared.
        const saved = await api.llmSettings.save(patch);
        setSetting(saved);
        setError(null);
        setNotice(`${what} saved.`);
      } catch (e) {
        // Leave `setting` alone so the control snaps back to what is actually
        // in effect rather than the value that failed to save.
        setError(message(e));
      } finally {
        setSaving(false);
      }
    },
    [api],
  );

  const sourceLine = () => {
    if (!setting) return "";
    if (setting.source === "company") {
      return "Set for this organization.";
    }
    if (setting.source === "platform") {
      return "Inherited from the deployment default.";
    }
    return "No choice anywhere — using the built-in default.";
  };

  return (
    // The AI model tab's section scope; display: contents keeps it out of layout.
    <div data-ph-scope="ai_model" style={{ display: "contents" }}>
      <PageHeader title="AI model" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <WorkspaceCard interactive={false}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Provider and model
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Which model answers this organization&apos;s chats. Leave a field on
              the default to follow the deployment-wide setting.
            </Typography>
          </Box>

          <FormControl
            data-ph-scope="provider"
            size="small"
            sx={{ maxWidth: 320 }}
            disabled={saving || !setting}
          >
            <InputLabel id="llm-provider-label">Provider</InputLabel>
            <Select
              data-ph="open"
              // Portaled out of the card, so the menu names its whole path (D7).
              MenuProps={{ slotProps: { root: { "data-ph-scope": "organization-ai_model-provider" } as object } }}
              labelId="llm-provider-label"
              label="Provider"
              value={setting?.provider ?? INHERIT}
              onChange={(e) =>
                void save(
                  {
                    provider:
                      e.target.value === INHERIT ? null : String(e.target.value),
                  },
                  "Provider",
                )
              }
              SelectDisplayProps={{ "aria-label": "AI provider" }}
            >
              <MenuItem data-ph="select" value={INHERIT}>
                <em>Use the default</em>
              </MenuItem>
              {(setting?.options.providers ?? []).map((id) => (
                <MenuItem data-ph="select" key={id} value={id}>
                  {PROVIDER_LABELS[id] ?? id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            data-ph-scope="model"
            size="small"
            sx={{ maxWidth: 420 }}
            disabled={saving || !setting}
          >
            <InputLabel id="llm-model-label">Model</InputLabel>
            <Select
              data-ph="open"
              MenuProps={{ slotProps: { root: { "data-ph-scope": "organization-ai_model-model" } as object } }}
              labelId="llm-model-label"
              label="Model"
              value={setting?.model ?? INHERIT}
              onChange={(e) =>
                void save(
                  {
                    model: e.target.value === INHERIT ? null : String(e.target.value),
                  },
                  "Model",
                )
              }
              SelectDisplayProps={{ "aria-label": "AI model" }}
            >
              <MenuItem data-ph="select" value={INHERIT}>
                <em>Use the default</em>
              </MenuItem>
              {(setting?.options.models ?? []).map((id) => (
                <MenuItem data-ph="select" key={id} value={id}>
                  {modelLabel(id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="number"
            label="Temperature"
            sx={{ maxWidth: 200 }}
            disabled={saving || !setting}
            defaultValue={setting?.temperature ?? ""}
            key={`temp-${setting?.temperature ?? "unset"}`}
            helperText="0 is deterministic. Blank follows the default."
            slotProps={{
              htmlInput: { min: 0, max: 2, step: 0.1, "aria-label": "Temperature" },
            }}
            onBlur={(e) => {
              const raw = e.target.value.trim();
              const next = raw === "" ? null : Number(raw);
              // Dirty-check: a blur without an edit must not write.
              if (next === (setting?.temperature ?? null)) return;
              void save({ temperature: next }, "Temperature");
            }}
          />

          {/* Source line: without it the dropdowns are ambiguous about whether
              anyone chose this value or it is merely inherited. */}
          {setting && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {sourceLine()}
              {setting.model ? ` Currently using ${modelLabel(setting.model)}.` : ""}
            </Typography>
          )}
        </Stack>
      </WorkspaceCard>
    </div>
  );
}
